/**
 * The Shell's window stacking order (q-demo-dialogue-playtest-2026-10-29).
 *
 * Windows used to take `z` from a counter that only grew (+1 per open or focus), so after enough
 * focusing a window would climb over every overlay meant to sit above it (the quest completion
 * card, the loading screen at 80, the guest screens at 95, the Menu sheet at 1000). Restacking keeps the band fixed: the open windows hold
 * `WINDOW_Z_BASE .. WINDOW_Z_BASE + n - 1`, in their order, with the raised one on top.
 */

/** The lowest window z. Overlays above windows only need to clear the band's top. */
export const WINDOW_Z_BASE = 10;
/** Every window z is below this: overlays meant to sit above windows use at least this value. */
export const WINDOW_Z_CEILING = 50;

/** Raise `topId` to the top and renumber every window into the fixed band, keeping their order. */
export function restack<T extends { id: string; z: number }>(ws: readonly T[], topId: string): T[] {
  const top = ws.find((w) => w.id === topId);
  const rest = ws.filter((w) => w.id !== topId).sort((a, b) => a.z - b.z);
  const ranked = top ? [...rest, top] : rest;
  const zOf = new Map(
    ranked.map((w, i) => [w.id, Math.min(WINDOW_Z_BASE + i, WINDOW_Z_CEILING - 1)]),
  );
  return ws.map((w) => ({ ...w, z: zOf.get(w.id) ?? WINDOW_Z_BASE }));
}
